// job-schema.mapper.ts
/** Shared shape for job invite API + schema builder (SSR-safe). */
export type ApiJob = {
  id: number;
  job_id?: string;
  position: string;
  department?: string;
  company_name?: string;
  comapany_name?: string; // spelling variant in your payload
  company_logo_url?: string;
  work_location?: string; // e.g., "Chennai, India"
  employment_type?: string[]; // ["Part-Time Work", "Contract-Based Work"]
  work_mode?: string[]; // ["On-Site Work","Hybrid Work","Shift Work"]
  currency_code?: string; // "INR"
  salary_offer?: number; // 120000 (assume per YEAR unless you know otherwise)
  experience_level?: string; // "Fresher"
  minimum_education?: string; // "Bachelor’s Degree"
  job_overview?: string; // HTML
  key_responsibilities?: string; // HTML
  benefits_perks?: string[]; // ["Bonuses","Stock Options",...]
  interview_format?: string[]; // ["Offline Interview (In-Person Interview)"]
  available_vacancies?: number; // 10
  created_at?: string; // ISO
  due_date?: string; // "31-07-2025" (dd-mm-yyyy)
  start_date?: string; // "28-08-2025" (dd-mm-yyyy)
  language_proficiency?: string; // JSON string e.g. [["English","Fluent"]]
  languages?: { lang: string; level: string }[];
  expired?: number; // 1/0
  additional_notes?: string;
};

function stripHtml(input?: string): string | undefined {
  if (!input) return undefined;
  // quick & safe for SSR: remove tags + collapse whitespace
  return input
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+\n/g, "\n")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function parseDdMmYyyy(d?: string): string | undefined {
  if (!d) return undefined;
  // supports "31-07-2025" or "31/07/2025"
  const m = d.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (!m) return undefined;
  const [, dd, mm, yyyy] = m;
  const iso = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  return new Date(iso).toISOString();
}

function splitLocation(loc?: string): { locality?: string; country?: string } {
  if (!loc) return {};
  const [city, ...rest] = loc.split(",").map((s) => s.trim());
  const country = rest.join(", ") || undefined;
  return { locality: city || undefined, country: country || undefined };
}

function pickCompanyName(job: ApiJob): string | undefined {
  return job.company_name || job.comapany_name;
}

function toEmploymentType(et?: string[]): string[] | undefined {
  if (!et || !et.length) return undefined;
  // Keep as human-readable strings; schema.org allows array of strings
  return et;
}

function toJobLocationType(modes?: string[]): string | undefined {
  if (!modes || !modes.length) return undefined;
  // Map common phrases to schema.org JobLocationType keywords
  const s = modes.join(" ").toLowerCase();
  if (s.includes("remote")) return "TELECOMMUTE";
  if (s.includes("hybrid")) return "HYBRID";
  if (s.includes("on-site") || s.includes("onsite")) return "ON_SITE";
  return undefined; // optional
}

function parseLanguages(job: ApiJob): string[] | undefined {
  if (job.languages?.length) return job.languages.map((l) => l.lang);
  if (job.language_proficiency) {
    try {
      const x = JSON.parse(job.language_proficiency) as [string, string][];
      return x.map(([lang]) => lang);
    } catch {}
  }
  return undefined;
}

export function buildJobPostingSchema(
  job: ApiJob,
  opts: {
    siteName: string; // e.g., "UNIPREP"
    baseJobUrl: string; // e.g., "https://job.uniprep.ai/view"
    jobUrl?: string;
    salaryUnit?: "YEAR" | "MONTH" | "WEEK" | "DAY" | "HOUR";
  },
): any {
  const company = pickCompanyName(job) || opts.siteName;
  const descriptionText = [
    stripHtml(job.job_overview),
    stripHtml(job.key_responsibilities),
  ]
    .filter(Boolean)
    .join("\n\n");

  const { locality, country } = splitLocation(job.work_location);
  const jobUrl = opts.jobUrl || `${opts.baseJobUrl}/${job.id}`;

  // const schema: any = {
  //   '@context': 'https://schema.org',
  //   '@type': 'JobPosting',
  //   title: job.position,
  //   description: descriptionText,
  //   identifier: {
  //     '@type': 'PropertyValue',
  //     name: company,
  //     value: job.job_id || String(job.id)
  //   },
  //   hiringOrganization: {
  //     '@type': 'Organization',
  //     name: company,
  //     logo: job.company_logo_url
  //   },
  //   jobLocation: locality || country ? {
  //     '@type': 'Place',
  //     address: {
  //       '@type': 'PostalAddress',
  //       addressLocality: locality,
  //       addressCountry: country
  //     }
  //   } : undefined,
  //   jobLocationType: toJobLocationType(job.work_mode),
  //   employmentType: toEmploymentType(job.employment_type),
  //   datePosted: job.created_at ? new Date(job.created_at).toISOString() : undefined,
  //   validThrough: parseDdMmYyyy(job.due_date), // Google expects ISO8601
  //   // number of openings
  //   totalJobOpenings: job.available_vacancies,
  //   // education & experience
  //   educationRequirements: job.minimum_education,
  //   experienceRequirements:
  //     job.experience_level?.toLowerCase() === 'fresher' ? '0 years (Fresher)' : job.experience_level,
  //   // salary
  //   baseSalary: (job.salary_offer && job.currency_code) ? {
  //     '@type': 'MonetaryAmount',
  //     currency: job.currency_code,
  //     value: {
  //       '@type': 'QuantitativeValue',
  //       value: job.salary_offer,
  //       unitText: opts.salaryUnit || 'YEAR'
  //     }
  //   } : undefined,
  //   // bonuses & benefits
  //   jobBenefits: job.benefits_perks?.join(', '),
  //   // interview formats
  //   applicationContact: job.interview_format?.length ? {
  //     '@type': 'ContactPoint',
  //     contactType: job.interview_format.join(', ')
  //   } : undefined,
  //   // languages
  //   inLanguage: parseLanguages(job),
  //   // extra (optional)
  //   // startDate: parseDdMmYyyy(job.start_date), // include if you want
  //   // applicantLocationRequirements: ...
  //   // directApply: true/false
  // };

  const workModeText = job.work_mode?.join(" ").toLowerCase() ?? "";
  const isRemote =
    workModeText.includes("remote") || workModeText.includes("work from home");

  const schema: any = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.position,
    url: jobUrl,
    description: descriptionText,
    identifier: {
      "@type": "PropertyValue",
      name: company,
      value: job.job_id || String(job.id),
    },
    hiringOrganization: {
      "@type": "Organization",
      name: company,
      logo: job.company_logo_url,
    },
    // 1. PHYSICAL LOCATION: Keep this for On-site/Hybrid
    jobLocation:
      locality || country
        ? {
            "@type": "Place",
            address: {
              "@type": "PostalAddress",
              addressLocality: locality,
              addressCountry: country || "IN", // Default to your primary country if missing
            },
          }
        : undefined,

    // 2. REMOTE STATUS: Mark as TELECOMMUTE if remote
    jobLocationType: isRemote ? "TELECOMMUTE" : undefined,

    // 3. THE FIX: Required by Google for all Remote jobs
    applicantLocationRequirements: isRemote
      ? {
          "@type": "Country",
          name: country || "IN", // Even for remote, you must specify the eligible country
        }
      : undefined,

    employmentType: toEmploymentType(job.employment_type),
    datePosted: job.created_at
      ? new Date(job.created_at).toISOString()
      : undefined,
    validThrough: parseDdMmYyyy(job.due_date),

    totalJobOpenings: job.available_vacancies,

    // Cleaned up experience for Google's parser
    experienceRequirements: {
      "@type": "OccupationalExperienceRequirements",
      monthsOfExperience:
        job.experience_level?.toLowerCase() === "fresher"
          ? 0
          : parseInt(job.experience_level ?? "0", 10) * 12 || 0,
    },

    baseSalary:
      job.salary_offer && job.currency_code
        ? {
            "@type": "MonetaryAmount",
            currency: job.currency_code,
            value: {
              "@type": "QuantitativeValue",
              value: job.salary_offer,
              unitText: opts.salaryUnit || "YEAR",
            },
          }
        : undefined,

    jobBenefits: job.benefits_perks?.join(", "),

    // Highly recommended to fix "non-critical" warnings
    directApply: true,

    inLanguage: parseLanguages(job),
  };

  // Tidy: remove undefined keys
  Object.keys(schema).forEach(
    (k) => schema[k] === undefined && delete schema[k],
  );
  if (schema.hiringOrganization) {
    Object.keys(schema.hiringOrganization).forEach(
      (k) =>
        schema.hiringOrganization[k] === undefined &&
        delete schema.hiringOrganization[k],
    );
  }
  if (schema.jobLocation && schema.jobLocation.address) {
    Object.keys(schema.jobLocation.address).forEach(
      (k) =>
        schema.jobLocation.address[k] === undefined &&
        delete schema.jobLocation.address[k],
    );
  }
  return schema;
}
